import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, Sparkles, Building2, UserPlus, MessageSquare, Loader2, ArrowRight, Check } from 'lucide-react';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/contexts/AuthContext';

// ── WelcomeModal ────────────────────────────────────────────────────────
//
// First-time user onboarding modal. Shows once per user (localStorage
// flag), 4 slides with skip-per-slide + X-close options. Guides brand-
// new tenants past the empty-dashboard scary state — they finish with
// a workspace name, know how to add a lead, and know WhatsApp exists.
//
// LocalStorage key includes user id so multiple sessions on same
// browser (multi-account) don't stomp each other's onboarding state.

const STORAGE_PREFIX = 'brixlead:welcome:';

type SlideKey = 'welcome' | 'workspace' | 'firstLead' | 'whatsapp';
const SLIDES: SlideKey[] = ['welcome', 'workspace', 'firstLead', 'whatsapp'];

export function WelcomeModal() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const [workspaceName, setWorkspaceName] = useState('');
  const [savingWorkspace, setSavingWorkspace] = useState(false);

  const storageKey = user?.id ? `${STORAGE_PREFIX}${user.id}` : null;

  // First-load check — show the modal exactly once per user per browser.
  // The "seen" flag is written the MOMENT the modal is shown, NOT only on
  // dismiss. Otherwise a user who closes the tab / refreshes / navigates
  // away mid-flow (without hitting X or finishing the slides) never sets
  // the flag, and the modal reappears every visit — the exact bug this
  // fixes. Being shown once IS the one-time event; the setup steps stay
  // re-accessible from Settings, so there's no downside to marking seen
  // on display.
  useEffect(() => {
    if (!isAuthenticated || !storageKey) return;
    const seen = typeof window !== 'undefined' && window.localStorage?.getItem(storageKey);
    if (!seen) {
      // Small delay so it doesn't fight for attention with the initial
      // login redirect + dashboard load. clearTimeout on unmount means a
      // fast navigate-away before it fires does NOT mark seen (it was
      // never actually shown) — the flag is only written once it opens.
      const t = setTimeout(() => {
        setOpen(true);
        try { window.localStorage.setItem(storageKey, '1'); } catch { /* storage disabled — worst case it shows again, harmless */ }
      }, 600);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isAuthenticated, storageKey]);

  // Prefill the workspace input with whatever's already on the tenant
  // (e.g. what they typed on the Register page as "Organization").
  useEffect(() => {
    if (!open) return;
    api.get('/tenant/me').then((r) => {
      const cur = (r.data as any)?.name ?? '';
      if (cur) setWorkspaceName(cur);
    }).catch(() => { /* silent — user can type fresh */ });
  }, [open]);

  const dismiss = () => {
    if (storageKey) window.localStorage.setItem(storageKey, '1');
    setOpen(false);
  };

  const next = () => {
    if (slideIndex < SLIDES.length - 1) setSlideIndex((i) => i + 1);
    else dismiss();
  };
  const back = () => setSlideIndex((i) => Math.max(0, i - 1));

  const saveWorkspaceAndNext = async () => {
    const trimmed = workspaceName.trim();
    if (!trimmed) { next(); return; } // treat empty as skip
    setSavingWorkspace(true);
    try {
      await api.patch('/tenant/me', { name: trimmed });
      toast('success', 'Workspace saved');
      next();
    } catch (err: any) {
      // Permission errors are silent-skip — new signups should always
      // pass but tenants revisiting via manual localStorage wipe might
      // not be admin. Don't block onboarding on this leg.
      toast('error', err?.response?.data?.message || 'Could not save workspace name');
      next();
    } finally {
      setSavingWorkspace(false);
    }
  };

  if (!open) return null;

  const slide = SLIDES[slideIndex];

  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header — progress dots + close */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-1.5">
            {SLIDES.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === slideIndex ? 'w-8 bg-[#6366F1]' : i < slideIndex ? 'w-1.5 bg-[#6366F1]/40' : 'w-1.5 bg-slate-200'
                }`}
              />
            ))}
          </div>
          <button
            onClick={dismiss}
            aria-label="Close welcome"
            className="text-slate-400 hover:text-slate-700 rounded p-1"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-8">
          {slide === 'welcome' && (
            <div className="text-center space-y-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center mx-auto shadow-lg shadow-indigo-200">
                <Sparkles className="h-7 w-7 text-white" />
              </div>
              <h2 className="text-2xl font-extrabold text-slate-900">Welcome to BrixLead 👋</h2>
              <p className="text-sm text-slate-600 max-w-sm mx-auto leading-relaxed">
                Let's set up your workspace in about 2 minutes. You can skip any step
                and come back later from Settings.
              </p>
            </div>
          )}

          {slide === 'workspace' && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                  <Building2 className="h-5 w-5 text-[#6366F1]" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Name your workspace</h2>
                  <p className="text-xs text-slate-500">This is what your team sees in the sidebar.</p>
                </div>
              </div>
              <div>
                <label className="block text-xs font-normal text-slate-600 mb-1.5">Workspace / Company name</label>
                <input
                  type="text"
                  value={workspaceName}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                  placeholder="e.g. Sunrise Real Estate"
                  className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]/40"
                  autoFocus
                />
                <p className="text-[11px] text-slate-400 mt-1.5">You can change this anytime in Settings → Brand.</p>
              </div>
            </div>
          )}

          {slide === 'firstLead' && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <UserPlus className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Add your first lead</h2>
                  <p className="text-xs text-slate-500">One click to open the New Lead form.</p>
                </div>
              </div>
              <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 space-y-2">
                <p className="text-sm text-slate-600 leading-relaxed">
                  A <b>lead</b> is anyone who's shown interest — a form submission,
                  a WhatsApp reply, an inbound call. BrixLead auto-assigns leads
                  to team members and tracks every follow-up.
                </p>
                <ul className="text-xs text-slate-500 space-y-1 pl-4 list-disc">
                  <li>Or leave leads empty — new leads land here automatically from Facebook Ads, forms, or API.</li>
                </ul>
              </div>
              <button
                onClick={() => { dismiss(); navigate('/leads?new=1'); }}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2.5"
              >
                <UserPlus className="h-4 w-4" /> Add a lead now
              </button>
            </div>
          )}

          {slide === 'whatsapp' && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Connect WhatsApp</h2>
                  <p className="text-xs text-slate-500">Optional — send automated follow-ups to leads.</p>
                </div>
              </div>
              <div className="rounded-xl bg-slate-50 border border-slate-100 p-4">
                <p className="text-sm text-slate-600 leading-relaxed">
                  BrixLead uses Meta's WhatsApp Business API for real conversations
                  — templates, media, buttons, everything. Setup takes ~10 minutes
                  with a Meta Business account. You can do this later.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { dismiss(); navigate('/settings?tab=integrations'); }}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-4 py-2.5"
                >
                  <MessageSquare className="h-4 w-4" /> Connect now
                </button>
                <button
                  onClick={dismiss}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium px-4 py-2.5"
                >
                  <Check className="h-4 w-4" /> All done
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer — Back + Skip/Next controls. Skip flips-forward but
            never closes the modal until the last slide (that's the X). */}
        <div className="border-t border-slate-100 px-5 py-3 flex items-center justify-between flex-shrink-0">
          <button
            onClick={back}
            disabled={slideIndex === 0}
            className="text-sm font-medium text-slate-500 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed px-3 py-2"
          >
            Back
          </button>
          <div className="flex items-center gap-2">
            {slide === 'workspace' ? (
              <>
                <button
                  onClick={next}
                  className="text-sm font-medium text-slate-500 hover:text-slate-900 px-3 py-2"
                >
                  Skip
                </button>
                <button
                  onClick={saveWorkspaceAndNext}
                  disabled={savingWorkspace}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] disabled:opacity-50 px-4 py-2 text-sm font-semibold text-white"
                >
                  {savingWorkspace && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  Save &amp; continue <ArrowRight className="h-4 w-4" />
                </button>
              </>
            ) : slide === 'welcome' ? (
              <button
                onClick={next}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] px-4 py-2 text-sm font-semibold text-white"
              >
                Let's go <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={next}
                className="text-sm font-medium text-slate-500 hover:text-slate-900 px-3 py-2"
              >
                {slideIndex === SLIDES.length - 1 ? 'Close' : 'Skip'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
